import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  Dimensions,
  Image,
  LayoutChangeEvent,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import {
  isImageMimeType,
  isVideoMimeType,
} from '../../../services/mediaViewerService';

import type { CarouselMedia, CarouselSource } from './carouselMedia';

/** Carousel footer configuration - dimensions must match StyleSheet values below */
const FOOTER_CONFIG = {
  /** Width of filmstrip thumbnail in pixels */
  ITEM_WIDTH: 52,
  /** Gap between filmstrip items in pixels */
  ITEM_GAP: 6,
  /** Width of dot indicator in pixels */
  DOT_WIDTH: 6,
  /** Gap between dots in pixels */
  DOT_GAP: 6,
  /** Ignore parent-driven currentIndex changes for this duration after tap (ms).
   * This keeps the footer immune to Android's page-by-page scroll events. */
  TAP_LOCK_MS: 800,
} as const;

const ITEM_STRIDE = FOOTER_CONFIG.ITEM_WIDTH + FOOTER_CONFIG.ITEM_GAP;
const DOT_STRIDE = FOOTER_CONFIG.DOT_WIDTH + FOOTER_CONFIG.DOT_GAP;

// Symmetric padding so every item (including first/last) can be centered.
// scrollX = index × ITEM_STRIDE always places that item dead-centre.
function filmPaddingFor(containerWidth: number) {
  return Math.max(
    0,
    Math.floor(containerWidth / 2 - FOOTER_CONFIG.ITEM_WIDTH / 2),
  );
}
function dotPaddingFor(containerWidth: number) {
  return Math.max(
    0,
    Math.floor(containerWidth / 2 - FOOTER_CONFIG.DOT_WIDTH / 2),
  );
}

interface CarouselFooterProps {
  items: CarouselMedia[];
  currentIndex: number;
  onSelect: (index: number) => void;
  /** Where a thumbnail comes from for an item that carries none (GitLab #1539). */
  source: CarouselSource;
}

export const CarouselFooter: React.FC<CarouselFooterProps> = ({
  items,
  currentIndex,
  onSelect,
  source,
}) => {
  const dotsRef = useRef<ScrollView>(null);

  // scrollX drives the filmstrip translateX.
  // negativeScrollX is created once — recreating Animated.multiply() on every
  // render gives the native driver a new node each time, which resets the
  // in-flight animation to its start value and causes the zigzag.
  const scrollX = useRef(
    new Animated.Value(currentIndex * ITEM_STRIDE),
  ).current;
  const negativeScrollX = useRef(Animated.multiply(scrollX, -1)).current;

  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const currentIndexRef = useRef(currentIndex);

  const isFirstNavigation = useRef(true);
  const dotsReady = useRef(false);

  // Timestamp of the most recent thumbnail/dot tap.  See TAP_LOCK_MS above.
  const tapTimestamp = useRef(0);

  const [filmPadding, setFilmPadding] = useState(() =>
    filmPaddingFor(Dimensions.get('window').width),
  );
  const [dotsPadding, setDotsPadding] = useState(() =>
    dotPaddingFor(Dimensions.get('window').width),
  );

  // ─── Thumbnail lazy loading ─────────────────────────────────────────────────
  // An item that carries its own `thumbnailUri` needs nothing fetched. The rest
  // are asked of the source's `loadThumbnail`, when it has one (request
  // attachments fetch theirs on demand; a message file always carries its own).
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());
  const thumbnailFetchedRef = useRef<Set<string>>(new Set());
  const { loadThumbnail } = source;

  useEffect(() => {
    if (!loadThumbnail) return;
    const loadThumbnails = async () => {
      const toFetch = items.filter(item => {
        const mimeType = item.mimeType || '';
        const needsThumbnail =
          isImageMimeType(mimeType) || isVideoMimeType(mimeType);
        return (
          needsThumbnail &&
          !item.thumbnailUri &&
          !thumbnailFetchedRef.current.has(item.id)
        );
      });

      if (toFetch.length === 0) return;

      // Mark as fetching to prevent duplicate requests
      toFetch.forEach(item => thumbnailFetchedRef.current.add(item.id));

      const results = await Promise.all(
        toFetch.map(async item => {
          try {
            return { id: item.id, url: await loadThumbnail(item) };
          } catch {
            return { id: item.id, url: null };
          }
        }),
      );

      const newThumbnails = new Map<string, string>();
      results.forEach(({ id, url }) => {
        if (url) {
          newThumbnails.set(id, url);
        }
      });

      if (newThumbnails.size > 0) {
        setThumbnails(prev => {
          // Use explicit forEach instead of spread (works around RN Web Map iteration issues)
          const merged = new Map<string, string>();
          prev.forEach((value, key) => merged.set(key, value));
          newThumbnails.forEach((value, key) => merged.set(key, value));
          return merged;
        });
      }
    };

    loadThumbnails();
  }, [items, loadThumbnail]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  });

  // ─── Filmstrip centering ───────────────────────────────────────────────────

  const animateFilmToIndex = useCallback(
    (index: number, animated: boolean) => {
      const toValue = index * ITEM_STRIDE;
      if (animated) {
        Animated.timing(scrollX, {
          toValue,
          duration: 220,
          useNativeDriver: true,
        }).start();
      } else {
        scrollX.setValue(toValue);
      }
    },
    [scrollX],
  );

  // Snap immediately whenever the filmstrip is laid out / padding changes.
  useEffect(() => {
    if (filmPadding > 0) {
      animateFilmToIndex(currentIndexRef.current, false);
    }
  }, [filmPadding, animateFilmToIndex]);

  // Unified selection handler for thumbnail and dot taps.
  const selectItem = useCallback(
    (index: number) => {
      tapTimestamp.current = Date.now();
      setActiveIndex(index);
      animateFilmToIndex(index, true);
      onSelect(index);
    },
    [animateFilmToIndex, onSelect],
  );

  // Follow main carousel swipes.
  // If a tap happened recently (< TAP_LOCK_MS ago) the footer has already
  // started its own animation — ignore every parent currentIndex change
  // until the lock expires. This makes the component immune to Android's
  // page-by-page onMomentumScrollEnd bursts and any other rapid updates.
  useEffect(() => {
    if (isFirstNavigation.current) {
      isFirstNavigation.current = false;
      return;
    }
    if (Date.now() - tapTimestamp.current < FOOTER_CONFIG.TAP_LOCK_MS) return;
    animateFilmToIndex(currentIndex, true);
    setActiveIndex(currentIndex);
  }, [currentIndex, animateFilmToIndex]);

  // ─── Dots sync ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (dotsPadding > 0) {
      const animated = dotsReady.current;
      dotsReady.current = true;
      dotsRef.current?.scrollTo({ x: activeIndex * DOT_STRIDE, animated });
    }
  }, [activeIndex, dotsPadding]);

  // ─── Layout callbacks ──────────────────────────────────────────────────────

  const onFilmstripLayout = useCallback((e: LayoutChangeEvent) => {
    setFilmPadding(filmPaddingFor(e.nativeEvent.layout.width));
  }, []);

  const onDotsLayout = useCallback((e: LayoutChangeEvent) => {
    setDotsPadding(dotPaddingFor(e.nativeEvent.layout.width));
  }, []);

  // Memoise the dots content style so re-renders don't create a new object.
  const dotsContentStyle = useMemo(
    () => [styles.dots, { paddingHorizontal: dotsPadding }],
    [dotsPadding],
  );

  const getThumbnailUri = useCallback(
    (item: CarouselMedia) =>
      item.thumbnailUri ?? thumbnails.get(item.id) ?? null,
    [thumbnails],
  );

  return (
    <View style={styles.container}>
      {/* Dot row */}
      <ScrollView
        ref={dotsRef}
        horizontal
        scrollEnabled={false}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={dotsContentStyle}
        onLayout={onDotsLayout}
      >
        {items.map((_, i) => (
          <TouchableOpacity
            key={i}
            style={[styles.dot, i === activeIndex && styles.dotActive]}
            onPress={() => selectItem(i)}
            accessibilityRole="button"
            accessibilityLabel={`Go to item ${i + 1}`}
            testID="carousel-dot"
          />
        ))}
      </ScrollView>

      {/* Filmstrip — outer Animated.View owns only the stable transform so
          the native animation node is never replaced on re-renders.
          Inner View owns paddingHorizontal which changes only on layout. */}
      <View style={styles.filmstripViewport} onLayout={onFilmstripLayout}>
        <Animated.View style={{ transform: [{ translateX: negativeScrollX }] }}>
          <View
            style={[
              styles.filmstripContent,
              { paddingHorizontal: filmPadding },
            ]}
          >
            {items.map((item, i) => {
              const uri = getThumbnailUri(item);
              return (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.filmItem,
                    i === activeIndex && styles.filmItemActive,
                  ]}
                  onPress={() => selectItem(i)}
                  accessibilityRole="button"
                  accessibilityLabel={item.name}
                  testID="carousel-film-item"
                >
                  {uri ? (
                    <Image
                      source={{ uri }}
                      style={styles.filmThumb}
                      resizeMode="cover"
                    />
                  ) : (
                    <View style={styles.filmPlaceholder}>
                      <Text style={styles.filmPlaceholderText}>
                        {item.mimeType?.split('/')[1]?.toUpperCase() ?? 'FILE'}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'rgba(0,0,0,0.8)',
    paddingBottom: 8,
  },
  dots: {
    flexDirection: 'row',
    paddingVertical: 8,
    gap: 6,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.35)',
  },
  dotActive: {
    backgroundColor: '#fff',
    width: 10,
    borderRadius: 5,
  },
  filmstripViewport: {
    overflow: 'hidden',
    height: 44, // item 40 + paddingBottom 4
  },
  filmstripContent: {
    flexDirection: 'row',
    gap: 6,
    paddingBottom: 4,
  },
  filmItem: {
    width: 52,
    height: 40,
    borderRadius: 4,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.25)',
  },
  filmItemActive: {
    borderColor: '#fff',
  },
  filmThumb: {
    width: '100%',
    height: '100%',
  },
  filmPlaceholder: {
    flex: 1,
    backgroundColor: '#333',
    justifyContent: 'center',
    alignItems: 'center',
  },
  filmPlaceholderText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 8,
  },
});
